import { notify as baseNotify } from '@kyvg/vue3-notification';
import type { NotificationsOptions } from '@kyvg/vue3-notification';
import type { InjectionKey } from 'vue';

import * as novaCore from '@nova/core';
import notificationsHelper from '@satellite/helpers/notifications';

export type NotifyType = 'success' | 'error' | 'warn' | 'info';

export interface NotifyOptions {
  duration?: number;
  group?: string;
  clean?: boolean;
  actionText?: string | null;
  actionFn?: (() => void) | null;
  title?: string | null;
  speed?: number;
  closeOnClick?: boolean;
  ignoreDuplicates?: boolean;
  data?: unknown;
}

const DEFAULTS: Required<Pick<NotifyOptions, 'duration' | 'group' | 'clean'>> &
  Omit<NotifyOptions, 'duration' | 'group' | 'clean'> = {
  duration: 5000,
  group: 'top-notifications',
  clean: false,
  actionText: null,
  actionFn: null,
  title: null,
  speed: 250,
  closeOnClick: true,
  ignoreDuplicates: true,
  data: undefined
};

export type NotifyFn = (message: string, type?: NotifyType, options?: NotifyOptions) => void;

export const defaultNotify: NotifyFn = (message, type = 'success', options = {}) => {
  const opts: NotifyOptions = { ...DEFAULTS, ...options };

  const titleBase = opts.title ?? type;
  const upper = novaCore.upperFirst?.bind(novaCore) ?? ((x: string) => x);
  const sanitize = novaCore.sanitizeInput?.bind(novaCore) ?? ((x: string) => x);

  const safeTitle = sanitize(upper(String(titleBase)));
  const safeText = sanitize(String(message));

  const payload: NotificationsOptions = {
    group: opts.group,
    title: safeTitle,
    text: safeText,
    type,
    duration: opts.duration,
    speed: opts.speed,
    closeOnClick: opts.closeOnClick,
    ignoreDuplicates: opts.ignoreDuplicates,
    data: {
      ...(notificationsHelper.getNotificationConfig?.(type) ?? {}),
      ...opts,
      type
    },
    clean: opts.clean
  };
  baseNotify(payload);
};

/**
 * DI - use composable - injects using notify key
 */
export const NOTIFY_KEY: InjectionKey<NotifyFn> = Symbol.for('notify');
